import React, { useMemo, useState } from "react";
import { Select } from "flowbite-react";
import { useQuery } from "react-query";
import { DASHBOARDS } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import { FaCrown, FaExclamationTriangle } from "react-icons/fa";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonChart,
  SkeletonTable,
} from "./crm/Skeletons";

const Dashboard = () => {
  const dateOptions = useMemo(
    () => [
      {
        label: "Hoje",
        value: moment().format("yyyy-MM-DD"),
        description: "Últimas 24h",
      },
      {
        label: "Últimos 7 dias",
        value: moment().subtract(7, "days").format("yyyy-MM-DD"),
        description: "Resumo semanal",
      },
      {
        label: "Últimos 30 dias",
        value: moment().subtract(30, "days").format("yyyy-MM-DD"),
        description: "Mês completo",
      },
      {
        label: "Desde o início",
        value: "",
        description: "Base histórica completa",
      },
    ],
    [],
  );

  const [selectedDate, setSelectedDate] = useState(dateOptions[0].value);
  const [selectedAvgUsersPlan, setSelectedAvgUsersPlan] = useState("PRO");
  const [expiredModalPlan, setExpiredModalPlan] = useState(null);

  const {
    isLoading: isLoadingDashboardInfos,
    error: errorDashboardInfos,
    data: dashboardInfos,
  } = useQuery(
    ["dashboardInfos", selectedDate],
    async () => {
      const { data } = await axios.get(
        `${DASHBOARDS.OVERVIEW}?date=${selectedDate ?? ""}`,
      );
      return data;
    },
    { keepPreviousData: true },
  );

  const safeCount = (value) => {
    if (value === undefined || value === null) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const planCounts = dashboardInfos?.companiesCountByPlan ?? {};
  const freeCompanies = safeCount(planCounts?.FREE);
  const essentialCompanies = safeCount(planCounts?.ESSENTIAL);
  const proCompanies = safeCount(planCounts?.PRO);
  const planStatusBreakdown = dashboardInfos?.planStatusBreakdown ?? {};
  const proStatus = planStatusBreakdown?.PRO ?? {
    active: 0,
    expired: 0,
    expiredCompanies: [],
  };
  const essentialStatus = planStatusBreakdown?.ESSENTIAL ?? {
    active: 0,
    expired: 0,
    expiredCompanies: [],
  };
  const planStatusCards = [
    {
      planId: "PRO",
      status: proStatus,
      activeAccent: "text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
    {
      planId: "ESSENTIAL",
      status: essentialStatus,
      activeAccent: "text-orange-600",
      badge: "bg-orange-100 text-orange-700",
    },
  ];
  const expiredModalStatus =
    expiredModalPlan === "PRO"
      ? proStatus
      : expiredModalPlan === "ESSENTIAL"
        ? essentialStatus
        : null;

  const newUsersLast7Days = dashboardInfos?.newUsersLast7Days ?? {
    labels: [],
    counts: [],
  };
  const newUsersSeries = [
    { name: "Novos usuários", data: newUsersLast7Days?.counts ?? [] },
  ];

  const topCustomersPro = dashboardInfos?.topCustomers ?? [];
  const topCustomersFree = dashboardInfos?.topCustomersFree ?? [];
  const topCustomersEssential = dashboardInfos?.topCustomersEssential ?? [];
  const expiringCompanies =
    dashboardInfos?.companiesCloseToExpire?.companiesToExpire ?? [];
  const latestCustomers = dashboardInfos?.latestCustomers ?? [];
  const recentCustomersWithoutAppointments15d =
    dashboardInfos?.recentCustomersWithoutAppointments15d ?? {
      days: 15,
      count: 0,
      customers: [],
    };
  const avgUsersPerCompanyByPlan = dashboardInfos?.avgUsersPerCompanyByPlan ?? {
    PRO: {
      averageUsersPerCompany: 0,
      totalCompanies: 0,
      totalUsers: 0,
    },
    ESSENTIAL: {
      averageUsersPerCompany: 0,
      totalCompanies: 0,
      totalUsers: 0,
    },
  };
  const selectedAvgUsersData =
    avgUsersPerCompanyByPlan[selectedAvgUsersPlan] ??
    avgUsersPerCompanyByPlan.PRO;
  const topCompaniesByProfessionals = {
    PRO: dashboardInfos?.topCompaniesByProfessionalsPro ?? [],
    ESSENTIAL: dashboardInfos?.topCompaniesByProfessionalsEssential ?? [],
  };
  const selectedTopCompaniesByProfessionals =
    topCompaniesByProfessionals[selectedAvgUsersPlan] ?? [];

  const newUsersChartOptions = useMemo(() => {
    const categories = newUsersLast7Days?.labels ?? [];
    const formatValue = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric.toLocaleString("pt-BR") : "0";
    };
    return {
      chart: {
        type: "bar",
        toolbar: { show: false },
        background: "transparent",
      },
      colors: ["#0ea5e9"],
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: "46%",
          dataLabels: { position: "center" },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => formatValue(value),
        style: {
          colors: ["#ffffff"],
          fontSize: "11px",
          fontWeight: 700,
        },
        dropShadow: { enabled: false },
      },
      grid: { borderColor: "#e2e8f0", strokeDashArray: 4 },
      xaxis: {
        categories,
        labels: { style: { colors: "#94a3b8", fontSize: "11px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatValue(value),
          style: { colors: "#94a3b8", fontSize: "11px" },
        },
      },
      tooltip: {
        y: { formatter: (value) => `${formatValue(value)} novos usuários` },
      },
    };
  }, [newUsersLast7Days?.labels]);

  const selectedFilterDescription =
    dateOptions.find((o) => o.value === selectedDate)?.description ?? "";

  const formatNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric.toLocaleString("pt-BR") : "0";
  };

  const formatDecimal = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("pt-BR", {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : "0,0";
  };

  const buildWhatsappLink = (phoneNumber) => {
    const digitsOnly = String(phoneNumber ?? "").replace(/\D/g, "");
    if (!digitsOnly) return "";

    // If number comes without country code, assume Brazil (+55).
    const normalized = digitsOnly.startsWith("55")
      ? digitsOnly
      : `55${digitsOnly}`;

    return `https://wa.me/${normalized}`;
  };

  const renderTopCustomersList = (
    customers,
    keyPrefix,
    highlightFirst = false,
  ) => {
    if (!customers.length) {
      return (
        <p className="py-3 text-center text-sm text-slate-400">
          Nenhum dado neste período.
        </p>
      );
    }

    return customers.slice(0, 5).map((company, i) => (
      <div
        key={`${keyPrefix}-${company.companyId ?? i}`}
        className={`flex items-center gap-3 rounded-xl px-4 py-2.5 ${
          highlightFirst && i === 0
            ? "bg-orange-50 border border-orange-100"
            : "bg-slate-50"
        }`}
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            highlightFirst && i === 0
              ? "bg-orange-500 text-white"
              : "bg-slate-200 text-slate-600"
          }`}
        >
          {i + 1}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {company?.Company?.name ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-1 text-sm font-bold text-slate-700">
          {highlightFirst && i === 0 && (
            <FaCrown className="text-amber-400 h-3.5 w-3.5" />
          )}
          {formatNumber(safeCount(company?.appointmentCount))}
        </div>
      </div>
    ));
  };

  const getExpiryUrgency = (date) => {
    const days = moment(date).diff(moment(), "days");
    if (days <= 1)
      return {
        label: `${days}d`,
        classes: "bg-red-100 text-red-700 border border-red-200",
      };
    if (days <= 3)
      return {
        label: `${days}d`,
        classes: "bg-orange-100 text-orange-700 border border-orange-200",
      };
    return {
      label: `${days}d`,
      classes: "bg-yellow-100 text-yellow-700 border border-yellow-200",
    };
  };

  const planBadgeClasses = (planId) => {
    if (planId === "PRO") return "bg-rose-100 text-rose-700";
    if (planId === "ESSENTIAL") return "bg-orange-100 text-orange-700";
    return "bg-slate-100 text-slate-600";
  };

  return (
    <div className="relative">
      {/*
        Enquanto carrega, a tela mostra o ESQUELETO em vez de uma sobreposição
        sobre o conteúdo velho esmaecido: o conteúdo anterior semiapagado
        convida a clicar no que já não vale, e a opacidade não diz o que está
        chegando.
      */}
      {isLoadingDashboardInfos ? (
        <div className="space-y-4">
          <SkeletonHeader withAction={false} />
          <SkeletonKpiStrip count={4} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SkeletonChart height={220} bars={12} />
            <SkeletonChart height={220} bars={8} />
          </div>
          <SkeletonTable rows={6} cols={5} withAvatar={false} />
        </div>
      ) : null}

      <div
        className={`space-y-6 ${isLoadingDashboardInfos ? "hidden" : ""}`}
      >
        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 text-white shadow-xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-orange-400">
                Visão estratégica
              </p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight">
                Painel administrativo
              </h1>
              <p className="mt-1 text-sm text-slate-400">
                {selectedFilterDescription}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-slate-700">
            {[
              { label: "PRO", count: proCompanies, accent: "text-rose-400" },
              {
                label: "ESSENTIAL",
                count: essentialCompanies,
                accent: "text-orange-400",
              },
              { label: "FREE", count: freeCompanies, accent: "text-slate-400" },
            ].map((plan) => (
              <div
                key={plan.label}
                className="bg-slate-800/60 px-5 py-4 text-center"
              >
                <p className={`text-2xl font-bold ${plan.accent}`}>
                  {formatNumber(plan.count)}
                </p>
                <p className="mt-1 text-xs text-slate-500">{plan.label}</p>
              </div>
            ))}
          </div>
        </section>

        {errorDashboardInfos && (
          <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <FaExclamationTriangle />
            Falha ao carregar os dados. Tente novamente em alguns instantes.
          </div>
        )}

        {/* ── PLAN STATUS (active vs expired) ───────────────────── */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {planStatusCards.map(({ planId, status, activeAccent, badge }) => (
            <article
              key={`plan-status-${planId}`}
              className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                    Status do plano
                  </p>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {planId}
                  </h3>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${badge}`}
                >
                  {planId}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                  <p className="text-[0.65rem] uppercase tracking-[0.25em] text-emerald-700">
                    Ativos
                  </p>
                  <p className={`mt-1 text-3xl font-bold ${activeAccent}`}>
                    {formatNumber(safeCount(status?.active))}
                  </p>
                  <p className="text-xs text-slate-500">dentro da validade</p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    safeCount(status?.expired) > 0 &&
                    setExpiredModalPlan(planId)
                  }
                  disabled={safeCount(status?.expired) === 0}
                  className={`rounded-2xl border border-red-100 bg-red-50/60 p-4 text-left transition ${
                    safeCount(status?.expired) > 0
                      ? "cursor-pointer hover:border-red-300 hover:bg-red-50"
                      : "cursor-default opacity-70"
                  }`}
                >
                  <p className="text-[0.65rem] uppercase tracking-[0.25em] text-red-700">
                    Expirados
                  </p>
                  <p className="mt-1 text-3xl font-bold text-red-600">
                    {formatNumber(safeCount(status?.expired))}
                  </p>
                  <p className="text-xs text-slate-500">
                    {safeCount(status?.expired) > 0
                      ? "Clique para ver quem expirou"
                      : "Nenhum expirado"}
                  </p>
                </button>
              </div>
            </article>
          ))}
        </section>

        {/* ── CHARTS ────────────────────────────────────────────── */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Novos clientes
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Últimos 7 dias
                </h2>
              </div>
              <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-600">
                Diário
              </span>
            </div>
            <div className="mt-4">
              <ReactApexChart
                options={newUsersChartOptions}
                series={newUsersSeries}
                type="bar"
                height={280}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-amber-100 bg-amber-50/40 p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-amber-600">
                  Métrica de ativação
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Clientes sem agendamento
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Cadastrados nos últimos{" "}
                  {recentCustomersWithoutAppointments15d.days} dias
                </p>
              </div>
              <div className="rounded-2xl bg-white px-5 py-3 text-center shadow-sm border border-amber-100">
                <p className="text-[0.65rem] uppercase tracking-[0.25em] text-slate-400">
                  Quantidade
                </p>
                <p className="text-3xl font-bold text-amber-600">
                  {formatNumber(
                    safeCount(recentCustomersWithoutAppointments15d.count),
                  )}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                Últimos clientes sem agendamento
              </p>
              <div className="space-y-2">
                {recentCustomersWithoutAppointments15d.customers.length ? (
                  recentCustomersWithoutAppointments15d.customers
                    .slice(0, 5)
                    .map((customer) => (
                      <div
                        key={`no-appointment-${customer.id}`}
                        className="flex items-center justify-between rounded-xl bg-white px-4 py-2.5 border border-amber-100"
                      >
                        <div className="min-w-0 flex-1 pr-2">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {customer.name}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {customer.phoneNumber ?? "Sem telefone"}
                          </p>
                          <span
                            className={`text-[0.65rem] font-semibold px-1.5 py-0.5 rounded ${planBadgeClasses(
                              customer.planId,
                            )}`}
                          >
                            {customer.planId ?? "—"}
                          </span>
                        </div>
                        <span className="shrink-0 text-xs text-slate-500">
                          {moment(customer.createdAt).format("DD/MM/yy")}
                        </span>
                      </div>
                    ))
                ) : (
                  <p className="py-3 text-center text-sm text-slate-400">
                    Nenhum cliente sem agendamento neste período.
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* ── TOP CLIENTS BY PLAN ──────────────────────────────── */}
        <section className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Ranking
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  Top PRO
                </h3>
              </div>
              <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                PRO
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {renderTopCustomersList(topCustomersPro, "pro", true)}
            </div>
          </article>

          <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Ranking
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  Top ESSENTIAL
                </h3>
              </div>
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600">
                ESSENTIAL
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {renderTopCustomersList(topCustomersEssential, "essential")}
            </div>
          </article>

          <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Ranking
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  Top FREE
                </h3>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                FREE
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {renderTopCustomersList(topCustomersFree, "free")}
            </div>
          </article>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                Eficiência de plano
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Média de usuários por empresa
              </h2>
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={selectedAvgUsersPlan}
                onChange={(event) =>
                  setSelectedAvgUsersPlan(event.target.value)
                }
                className="text-sm"
              >
                <option value="PRO">PRO</option>
                <option value="ESSENTIAL">ESSENTIAL</option>
              </Select>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
              <p className="text-[0.65rem] uppercase tracking-[0.25em] text-sky-700">
                Média
              </p>
              <p className="mt-1 text-3xl font-bold text-sky-700">
                {formatDecimal(selectedAvgUsersData?.averageUsersPerCompany)}
              </p>
              <p className="text-xs text-slate-500">usuários por empresa</p>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-[0.65rem] uppercase tracking-[0.25em] text-slate-500">
                Empresas
              </p>
              <p className="mt-1 text-3xl font-bold text-slate-800">
                {formatNumber(selectedAvgUsersData?.totalCompanies)}
              </p>
              <p className="text-xs text-slate-500">
                plano {selectedAvgUsersPlan}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-[0.65rem] uppercase tracking-[0.25em] text-slate-500">
                Usuários
              </p>
              <p className="mt-1 text-3xl font-bold text-slate-800">
                {formatNumber(selectedAvgUsersData?.totalUsers)}
              </p>
              <p className="text-xs text-slate-500">total do plano filtrado</p>
            </div>
          </div>

          <div className="mt-5">
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
              Top clientes com mais profissionais
            </p>
            <div
              className="mt-2 space-y-2 overflow-y-auto pr-1"
              style={{ maxHeight: "300px" }}
            >
              {selectedTopCompaniesByProfessionals.length ? (
                selectedTopCompaniesByProfessionals
                  .slice(0, 15)
                  .map((company, index) => (
                    <div
                      key={`top-professionals-${selectedAvgUsersPlan}-${company.companyId ?? index}`}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                        {index + 1}
                      </span>
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                        {company?.Company?.name ?? "—"}
                      </p>
                      <span className="text-sm font-bold text-slate-700">
                        {formatNumber(safeCount(company?.professionalCount))}
                      </span>
                    </div>
                  ))
              ) : (
                <p className="py-3 text-center text-sm text-slate-400">
                  Nenhum cliente encontrado para este plano.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── BOTTOM TABLES ─────────────────────────────────────── */}
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Latest customers */}
          <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Novos
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  Últimos cadastros
                </h3>
              </div>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-indigo-600">
                {latestCustomers.length}
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {latestCustomers.length ? (
                latestCustomers.slice(0, 8).map((customer) => (
                  <div
                    key={`latest-${customer.id}`}
                    className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5"
                  >
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {customer.name}
                      </p>
                      <p className="truncate text-xs text-slate-500">
                        {customer.phoneNumber ?? "Sem telefone"}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-slate-500">
                      {moment(customer.createdAt).format("DD/MM/yy")}
                    </span>
                  </div>
                ))
              ) : (
                <p className="py-6 text-center text-sm text-slate-400">
                  Nenhum cliente recente.
                </p>
              )}
            </div>
          </article>

          {/* Expiring plans */}
          <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Alertas
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  Planos expirando
                </h3>
              </div>
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                  expiringCompanies.length > 0
                    ? "bg-red-50 text-red-600"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {expiringCompanies.length}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Empresas expirando nos próximos 5 dias
            </p>
            <div className="mt-4 space-y-2 overflow-y-auto pr-1">
              {expiringCompanies.length ? (
                expiringCompanies.slice(0, 6).map((company) => {
                  const urgency = getExpiryUrgency(company.planExpirationDate);
                  return (
                    <div
                      key={`expiring-${company.id}`}
                      className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {company.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {buildWhatsappLink(company.phoneNumber) ? (
                            <a
                              href={buildWhatsappLink(company.phoneNumber)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sky-600 hover:text-sky-700 hover:underline"
                            >
                              {company.phoneNumber}
                            </a>
                          ) : (
                            company.phoneNumber ?? "Sem telefone"
                          )}
                        </p>
                        <span
                          className={`mt-1 inline-block text-[0.65rem] font-semibold px-1.5 py-0.5 rounded ${planBadgeClasses(
                            company.planId,
                          )}`}
                        >
                          {company.planId ?? "—"}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span
                          className={`rounded-md px-2 py-0.5 text-xs font-bold ${urgency.classes}`}
                        >
                          {urgency.label}
                        </span>
                        <span className="text-[0.65rem] text-slate-400">
                          {moment(company.planExpirationDate).format(
                            "DD/MM/yy",
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="py-6 text-center text-sm text-slate-400">
                  Nenhuma empresa próxima de expirar.
                </p>
              )}
            </div>
          </article>
        </section>
      </div>

      {/* ── EXPIRED PLANS MODAL ───────────────────────────────── */}
      {expiredModalPlan && expiredModalStatus && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => setExpiredModalPlan(null)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                  Planos expirados
                </p>
                <h3 className="text-lg font-semibold text-slate-900">
                  {expiredModalPlan} · {formatNumber(expiredModalStatus.expired)}{" "}
                  expirados
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setExpiredModalPlan(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
              >
                ×
              </button>
            </div>

            <div className="space-y-2 overflow-y-auto px-5 py-4">
              {expiredModalStatus.expiredCompanies?.length ? (
                expiredModalStatus.expiredCompanies.map((company) => (
                  <div
                    key={`expired-modal-${company.id}`}
                    className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {company.name}
                      </p>
                      <p className="text-xs text-slate-400">
                        {buildWhatsappLink(company.phoneNumber) ? (
                          <a
                            href={buildWhatsappLink(company.phoneNumber)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-600 hover:text-sky-700 hover:underline"
                          >
                            {company.phoneNumber}
                          </a>
                        ) : (
                          company.phoneNumber ?? "Sem telefone"
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="rounded-md border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                        {formatNumber(safeCount(company.daysExpired))}{" "}
                        {safeCount(company.daysExpired) === 1 ? "dia" : "dias"}
                      </span>
                      <span className="text-[0.65rem] text-slate-400">
                        {moment(company.planExpirationDate).format("DD/MM/yy")}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="py-6 text-center text-sm text-slate-400">
                  Nenhuma empresa expirada.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
